import qs from 'qs';

import { json_headers, request } from './common';

const basePath = `${window.configs.API_HOST}/api/lab/admin-portal`;
const remoteConfigBasePath = `${window.configs.API_HOST}/api/lab`;

const LANGUAGES_REMOTE_CONFIG_KEY = 'languages';

export type LabAudienceListItem = { id: number; name: string };
export type ListLabAudiencesResponse = {
  status: number;
  data: {
    audiences: Array<LabAudienceListItem>;
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
};

export async function listLabAudiences(
  query: { page?: number; search?: string },
  options?: { signal?: AbortSignal },
): Promise<ListLabAudiencesResponse> {
  const queryString = qs.stringify(query, { skipNulls: true });
  return request(`${basePath}/audiences?${queryString}`, {
    method: 'GET',
    headers: json_headers,
    signal: options?.signal,
  });
}

type LabRemoteConfigResult = {
  key: string;
  value: unknown;
  error?: {
    key: string;
    type: string;
  };
};

type LabRemoteConfigResponse = {
  data?: Record<string, LabRemoteConfigResult>;
};

type RemoteConfigLanguage = {
  locale?: unknown;
  nativeName?: unknown;
};

export type LanguageOption = {
  value: string;
  label: string;
};

function toLanguageList(value: unknown, key: 'visibleLanguages' | 'preReleaseLanguages'): Array<RemoteConfigLanguage> {
  if (typeof value !== 'object' || value === null) {
    return [];
  }

  const list = (value as Record<string, unknown>)[key];
  return Array.isArray(list) ? (list as Array<RemoteConfigLanguage>) : [];
}

/**
 * Combines the visible and pre-release language lists from the staging lab `languages` remote config into a
 * de-duplicated set of options, so newly supported locales are picked up without a code change.
 */
function toLanguageOptions(value: unknown): Array<LanguageOption> {
  const optionsByLocale = new Map<string, LanguageOption>();

  [...toLanguageList(value, 'visibleLanguages'), ...toLanguageList(value, 'preReleaseLanguages')].forEach(
    (language) => {
      if (typeof language.locale !== 'string' || language.locale.trim() === '') {
        return;
      }

      const locale = language.locale.trim();
      const nativeName = typeof language.nativeName === 'string' ? language.nativeName.trim() : '';
      if (optionsByLocale.has(locale)) {
        return;
      }

      optionsByLocale.set(locale, {
        value: locale,
        label: nativeName || locale,
      });
    },
  );

  return Array.from(optionsByLocale.values());
}

export async function getLanguageOptions(): Promise<Array<LanguageOption>> {
  try {
    const response = (await request(`${remoteConfigBasePath}/remote-config`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify({ keys: [LANGUAGES_REMOTE_CONFIG_KEY] }),
    })) as LabRemoteConfigResponse;

    const languagesConfig = response.data?.[LANGUAGES_REMOTE_CONFIG_KEY];
    if (!languagesConfig || languagesConfig.error) {
      return [];
    }

    return toLanguageOptions(languagesConfig.value);
  } catch {
    return [];
  }
}
